import { useEffect, useState } from 'react'
import { homeTab, needsOnboarding } from '@romain130492/topchopsticks'
import PageLayout from './components/PageLayout'
import ToastHost from './components/ToastHost'
import LoadingState from './components/LoadingState'
import EventsPage from './pages/EventsPage'
import SixPage from './pages/SixPage'
import ProfilePage from './pages/ProfilePage'
import SmsLoginPage from './pages/SmsLoginPage'
import OnboardingFlow from './pages/OnboardingFlow'
import EventDetailPage from './pages/EventDetailPage'
import { resumeSession, useSession } from './hooks/useSession'
import { showToast } from './lib/toast'
import type { TabKey } from './components/TabBar'

/**
 * The whole app, one screen at a time — deliberately mirroring the mini
 * program's single-page host rather than a router-per-page web app, so the two
 * flows stay identical.
 *
 * Two rules carried over, both learned the hard way in WeChat review:
 *   1. NOTHING signs anyone in on mount. Guests browse; a session begins only
 *      when someone enters an SMS code.
 *   2. The tab bar is visible on EVERY screen, login and onboarding included,
 *      so nobody is ever trapped.
 */
export default function App() {
  const [tab, setTab] = useState<TabKey>('dining')
  const [registering, setRegistering] = useState(false)
  /** Set by the triple-tap shortcut or a shared /event/:id link. */
  const [openEventId, setOpenEventId] = useState<string | null>(() => {
    const match = window.location.pathname.match(/^\/event\/(.+)$/)
    return match ? match[1] : null
  })
  const { loading, profile, application, registered } = useSession()

  useEffect(() => { resumeSession() }, [])

  /** Every "you need an account for this" tap ends here. */
  const startRegistration = (reason?: string) => {
    // Belt and braces after the "Objects are not valid as a React child"
    // crash: only a real string is ever shown.
    if (typeof reason === 'string' && reason) showToast({ title: reason, icon: 'none' })
    setTab('apply')          // light up Apply: joining IS the application
    setRegistering(true)
  }

  const requestTab = (next: TabKey) => {
    setOpenEventId(null)     // leaving a detail page
    if (next === 'apply' && !registered) return startRegistration()
    // Leaving the join flow. This IS the cancel button WeChat asks for.
    setRegistering(false)
    setTab(next)
  }

  const body = (() => {
    if (loading) return <LoadingState />

    if (registering || (tab === 'apply' && !registered)) {
      // Not signed in yet -> phone. Signed in but unfinished -> questionnaire.
      if (!profile) {
        return <SmsLoginPage onDone={() => { /* session hook re-renders us */ }} />
      }
      // Pass the application: a submitted questionnaire IS finished
      // onboarding, even when the phase PATCH never landed.
      if (needsOnboarding(profile, application)) {
        return (
          <OnboardingFlow
            onExit={() => { setRegistering(false); setTab('dining') }}
            onComplete={() => { setRegistering(false); setTab(homeTab(application)) }}
          />
        )
      }
      setRegistering(false)
    }

    if (openEventId) {
      return (
        <EventDetailPage
          eventId={openEventId}
          registered={registered}
          onRegister={startRegistration}
          onBack={() => { setOpenEventId(null); window.history.pushState({}, '', '/') }}
        />
      )
    }

    if (tab === 'dining') {
      return (
        <EventsPage
          registered={registered}
          onRegister={startRegistration}
          onOpenEvent={(id) => { setOpenEventId(id); window.history.pushState({}, '', `/event/${id}`) }}
        />
      )
    }
    if (tab === 'six') return <SixPage registered={registered} onRegister={startRegistration} onSeeDining={() => setTab('dining')} />
    if (tab === 'profile') {
      return (
        <ProfilePage
          onRegister={startRegistration}
          // "Update my application" reopens the questionnaire, which is the
          // same flow as joining — it resumes at the first unanswered question
          // rather than starting over, so this is a re-entry, not a reset.
          onEditApplication={() => setRegistering(true)}
        />
      )
    }
    return (
      <EventsPage
        registered={registered}
        onRegister={startRegistration}
        onOpenEvent={(id) => { setOpenEventId(id); window.history.pushState({}, '', `/event/${id}`) }}
      />
    )
  })()

  return (
    <>
      {/* The logo is a home button and home is the FEED. Routing it through
          homeTab() sent a guest to Apply, i.e. tapping the logo started
          registration — the opposite of an escape hatch. */}
      <PageLayout active={tab} onTab={requestTab} onHome={() => requestTab('dining')}>
        {body}
      </PageLayout>
      <ToastHost />
    </>
  )
}
